import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/app/AppHeader";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  User as UserIcon, Lock, CreditCard, Bell, Mail, Sparkles, Crown, Zap,
  ShieldCheck, ArrowUpRight, CheckCircle2, AlertCircle, LogOut, Loader2,
} from "lucide-react";
import { milestoneToastsEnabled, setMilestoneToastsEnabled } from "@/lib/local-prefs";

const PLAN_META = {
  free:    { label: "Free",    Icon: Zap,      cls: "border-foreground/30 text-foreground" },
  starter: { label: "Starter", Icon: Sparkles, cls: "border-primary text-primary bg-primary/5" },
  pro:     { label: "Pro",     Icon: Sparkles, cls: "border-primary text-primary bg-primary/5" },
  agency:  { label: "Agency",  Icon: Crown,    cls: "border-amber-500 text-amber-700 bg-amber-50" },
};

function SettingsSection({ icon: Icon, title, description, children, testid }) {
  return (
    <section className="border border-border bg-white p-6 md:p-8" data-testid={testid}>
      <div className="flex items-start gap-4 mb-6">
        <div className="w-10 h-10 bg-foreground text-background grid place-items-center flex-shrink-0">
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div>
          <h2 className="font-display tracking-tight text-xl">{title}</h2>
          {description && (
            <p className="text-sm text-muted-foreground mt-0.5">{description}</p>
          )}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [busyProfile, setBusyProfile] = useState(false);

  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [busyPwd, setBusyPwd] = useState(false);

  const [digestEnabled, setDigestEnabled] = useState(true);
  const [milestoneOn, setMilestoneOn] = useState(true);
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    if (user && typeof user === "object") {
      setName(user.name || "");
      setMilestoneOn(milestoneToastsEnabled());
      api.get("/me/digest-prefs").then(({ data }) => setDigestEnabled(!!data.enabled)).catch(() => {});
    }
  }, [user]);

  if (!user || typeof user !== "object") {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <main className="max-w-3xl mx-auto px-4 md:px-8 py-10">
          <div className="text-sm font-mono-data text-muted-foreground">Loading…</div>
        </main>
      </div>
    );
  }

  const plan = user.plan || "free";
  const pm = PLAN_META[plan] || PLAN_META.free;
  const PIcon = pm.Icon;

  async function saveProfile(e) {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setBusyProfile(true);
    try {
      await api.patch("/auth/me", { name: name.trim() });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyProfile(false);
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    if (newPwd.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error("Passwords don't match");
      return;
    }
    setBusyPwd(true);
    try {
      await api.post("/auth/me/password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
      toast.success("Password changed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyPwd(false);
    }
  }

  async function openPortal() {
    setOpeningPortal(true);
    try {
      const { data } = await api.post("/billing/portal", { return_url: `${window.location.origin}/app/settings` });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't open billing portal");
    } finally {
      setOpeningPortal(false);
    }
  }

  async function toggleDigest(next) {
    const prev = digestEnabled;
    setDigestEnabled(next);
    try {
      await api.post("/me/digest-prefs", { enabled: next });
      toast.success(next ? "Weekly digest emails on" : "Weekly digest emails off");
    } catch (e) {
      setDigestEnabled(prev);
      toast.error(formatApiError(e));
    }
  }

  function toggleMilestoneToasts(next) {
    setMilestoneOn(next);
    setMilestoneToastsEnabled(next);
    toast.success(next ? "Milestone toasts on" : "Milestone toasts off");
  }

  async function onLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-4 md:px-8 py-10 space-y-6">
        <div className="mb-4">
          <p className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">// account</p>
          <h1 className="font-semibold tracking-tight text-4xl md:text-5xl mt-2">Settings.</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Profile, password, plan, and notification preferences.
          </p>
        </div>

        {/* Profile */}
        <SettingsSection icon={UserIcon} title="Profile" description="How you appear in ArloSEO." testid="settings-profile">
          <form onSubmit={saveProfile} className="space-y-4 max-w-md">
            <div className="space-y-1.5">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Email</Label>
              <div className="flex items-center gap-2 h-10 px-3 border border-border bg-secondary/30 text-sm font-mono-data" data-testid="settings-email">
                <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="truncate">{user.email}</span>
                {user.email_verified ? (
                  <span className="ml-auto inline-flex items-center gap-1 text-emerald-700 text-xs">
                    <CheckCircle2 className="w-3 h-3" /> verified
                  </span>
                ) : (
                  <span className="ml-auto inline-flex items-center gap-1 text-amber-700 text-xs">
                    <AlertCircle className="w-3 h-3" /> unverified
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Display name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
                data-testid="settings-name-input"
                placeholder="Your name"
              />
            </div>
            <Button
              type="submit"
              disabled={busyProfile || name.trim() === (user.name || "").trim()}
              className="rounded-none bg-foreground hover:bg-foreground/90"
              data-testid="settings-save-profile"
            >
              {busyProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save profile
            </Button>
          </form>
        </SettingsSection>

        {/* Plan & Billing */}
        <SettingsSection icon={CreditCard} title="Plan & billing" description="Manage your subscription and payment method." testid="settings-billing">
          <div className="space-y-4 max-w-md">
            <div className={`inline-flex items-center gap-2 border px-3 py-1.5 ${pm.cls}`} data-testid="settings-plan-badge">
              <PIcon className="w-4 h-4" />
              <span className="font-semibold tracking-tight">{pm.label}</span>
              {user.on_trial && (
                <span className="font-mono-data text-[10px] uppercase tracking-widest opacity-70">
                  · trial · {user.trial_days_remaining}d left
                </span>
              )}
            </div>
            {plan === "free" ? (
              <Button
                onClick={() => navigate("/pricing")}
                className="rounded-none bg-primary text-background hover:bg-primary/90"
                data-testid="settings-upgrade-btn"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                See plans & upgrade
              </Button>
            ) : (
              <Button
                onClick={openPortal}
                disabled={openingPortal}
                variant="outline"
                className="rounded-none"
                data-testid="settings-manage-billing-btn"
              >
                {openingPortal ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Manage billing & invoices
                <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>
        </SettingsSection>

        {/* Password */}
        <SettingsSection icon={Lock} title="Password" description="Change your account password." testid="settings-password">
          <form onSubmit={changePassword} className="space-y-4 max-w-md">
            <div className="space-y-1.5">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Current password</Label>
              <Input
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                required
                autoComplete="current-password"
                className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
                data-testid="settings-current-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-data text-xs uppercase tracking-widest">New password</Label>
              <Input
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
                data-testid="settings-new-password"
              />
              <p className="font-mono-data text-[10px] text-muted-foreground uppercase tracking-widest">min 8 characters</p>
            </div>
            <div className="space-y-1.5">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Confirm new password</Label>
              <Input
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
                autoComplete="new-password"
                className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
                data-testid="settings-confirm-password"
              />
            </div>
            <Button
              type="submit"
              disabled={busyPwd || !currentPwd || !newPwd || !confirmPwd}
              className="rounded-none bg-foreground hover:bg-foreground/90"
              data-testid="settings-change-password-btn"
            >
              {busyPwd ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
              Change password
            </Button>
          </form>
        </SettingsSection>

        {/* Notifications */}
        <SettingsSection icon={Bell} title="Notifications" description="Control what shows up in your inbox and on screen." testid="settings-notifications">
          <div className="space-y-5 max-w-md">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm font-medium">Weekly digest email</div>
                <div className="text-xs text-muted-foreground mt-0.5">A summary of your project&apos;s SEO progress every Monday.</div>
              </div>
              <Switch
                checked={digestEnabled}
                onCheckedChange={toggleDigest}
                data-testid="settings-digest-toggle"
              />
            </div>
            <div className="flex items-center justify-between gap-4 pt-4 border-t border-dashed border-foreground/15">
              <div>
                <div className="text-sm font-medium">Milestone toasts</div>
                <div className="text-xs text-muted-foreground mt-0.5">Pop-up celebrations when you hit a streak milestone.</div>
              </div>
              <Switch
                checked={milestoneOn}
                onCheckedChange={toggleMilestoneToasts}
                data-testid="settings-milestone-toggle"
              />
            </div>
          </div>
        </SettingsSection>

        {/* Admin shortcut (only visible to admin) */}
        {user.role === "admin" && (
          <SettingsSection icon={ShieldCheck} title="Operator tools" description="Internal admin console — not visible to regular users." testid="settings-admin">
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => navigate("/admin")}
                variant="outline"
                className="rounded-none"
                data-testid="settings-open-admin"
              >
                <ShieldCheck className="w-4 h-4 mr-2" /> Open admin console
              </Button>
              <Button
                onClick={() => navigate("/admin/metrics")}
                variant="outline"
                className="rounded-none"
                data-testid="settings-open-metrics"
              >
                Metrics & observability
                <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </SettingsSection>
        )}

        {/* Danger / sign out */}
        <SettingsSection icon={LogOut} title="Sign out" description="Ends this session on this device." testid="settings-session">
          <Button
            onClick={onLogout}
            variant="outline"
            className="rounded-none text-rose-700 border-rose-200 hover:bg-rose-50"
            data-testid="settings-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </SettingsSection>
      </main>
    </div>
  );
}
